/**
 * DiscoveredContactsSection (TD-2106, Contact Enrichment Stream B).
 *
 * Renders the ephemeral discovery results as a grid of ContactCard, kept in a
 * SEPARATE section below the stored contacts so users can tell discovered people
 * apart from canonical CREFO ones (AC13).
 *
 * The section is introduced by a single centered "Discovered contacts" separator
 * line rather than a per-card badge — per @Hamid's PR-944 review: a recurring
 * marking on every card is redundant when one divider communicates the boundary.
 *
 * Each card is rendered with `source="discovered"` (which tailors the field set —
 * Age / shareholder show 'n/a', Country shows the Surfe value (TD-2358/TD-2273),
 * the job title renders as a grey line under the name, and the reveal buttons
 * route through /enrichment/discover-reveal). `access` is the
 * SINGLE hoisted useEnrichmentAccess() instance owned by KeyContactsTab and is
 * threaded down so each card does not fire its own /subscription-status + reveal
 * fetch (TD-2263 pattern). `companyId` is the CREFO profile id used as the
 * discover-reveal `profileId` and for Surfe company scoping.
 *
 * Discovered contacts have no CREFO profile page, so no view-details action is
 * offered; the LinkedIn action is passed through from the parent.
 */

import PropTypes from 'prop-types';
import ContactCard from '../../../contactCard';
import { contactKey } from './keyContactsUtils';

const cardKey = (contact, idx) => contact?.person_crefo_id ?? contact?.member_id ?? idx;

const DiscoveredContactsSection = ({
  contacts = [],
  onLinkedInClick,
  access,
  companyId,
  selectedIds,
  onSelectionChange,
  hiddenByFilters = 0,
  onClearFilters,
  onRevealSuccess,
  bulkRevealing
}) => {
  // TD-2085 Phase 6 (AC-019): discovered contacts carry only identity + role, so
  // an active Country/Age/Shareholder filter can hide every result. When the
  // visible list is empty BUT the filters hid discovered results, explain that
  // (with a "Clear filters" affordance) instead of silently rendering nothing.
  const count = contacts?.length ?? 0;
  const allHiddenByFilters = count === 0 && hiddenByFilters > 0;
  if (!count && !allHiddenByFilters) return null;

  return (
    <div className="discovered-contacts-section">
      <div className="discovered-contacts-separator">
        <h3 className="discovered-contacts-separator-label">Discovered contacts</h3>
      </div>
      {allHiddenByFilters ? (
        <div className="discovered-contacts-filtered-note" role="status">
          <span>
            {hiddenByFilters} discovered {hiddenByFilters === 1 ? 'contact is' : 'contacts are'}{' '}
            hidden by the active filters.
          </span>
          {onClearFilters && (
            <button
              type="button"
              className="discovered-contacts-clear-filters"
              onClick={onClearFilters}
            >
              Clear filters
            </button>
          )}
        </div>
      ) : (
        <div className="discovered-contacts-container">
          {contacts.map((contact, idx) => (
            <div className="discovered-contact-card-wrap" key={cardKey(contact, idx)}>
              <ContactCard
                contact={contact}
                source="discovered"
                access={access}
                companyId={companyId}
                onRevealSuccess={onRevealSuccess}
                // TD-2421: discovered cards join the bulk selection. The card
                // gates its own checkbox on identity (needs first + last name).
                selected={selectedIds?.has(contactKey(contact)) ?? false}
                onSelectionChange={onSelectionChange}
                // TD-2427: forward the in-flight bulk overlay so a discovered card
                // shows the reveal spinner during a bulk job (matched by contactKey).
                bulkRevealing={bulkRevealing}
                onLinkedInClick={() => onLinkedInClick?.(contact)}
              />
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

DiscoveredContactsSection.propTypes = {
  contacts: PropTypes.arrayOf(PropTypes.object),
  onLinkedInClick: PropTypes.func,
  // Hoisted enrichment access from KeyContactsTab so each discovered card does
  // not mount its own useEnrichmentAccess()/reveal fetch (TD-2263 pattern).
  access: PropTypes.shape({
    enabled: PropTypes.bool,
    limitReached: PropTypes.bool
  }),
  // CREFO company id — used as the discover-reveal `profileId` + Surfe scoping.
  companyId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  // TD-2421: shared bulk-selection state (keyed by contactKey) + change handler,
  // threaded from KeyContactsTab so discovered cards select alongside stored ones.
  selectedIds: PropTypes.instanceOf(Set),
  onSelectionChange: PropTypes.func,
  // AC-019: count of deduped discovered results the active filters hid. When the
  // visible list is empty but this is > 0, the section shows an explanatory notice
  // instead of vanishing.
  hiddenByFilters: PropTypes.number,
  // Clears all active Key Contacts filters (from the parent's reset handler).
  onClearFilters: PropTypes.func,
  // TD-2455: forwarded to each discovered card so a successful reveal refreshes
  // the credit counter KeyContactsTab owns (same handler as the stored cards).
  onRevealSuccess: PropTypes.func,
  // TD-2427: in-flight bulk reveal overlay (keys + fields) from KeyContactsTab,
  // forwarded verbatim to each card so discovered contacts animate during a job.
  bulkRevealing: PropTypes.shape({
    keys: PropTypes.instanceOf(Set),
    fields: PropTypes.arrayOf(PropTypes.oneOf(['email', 'phone']))
  })
};

export default DiscoveredContactsSection;
